// priority-builder.js — visual rule builder for the Priority page.
//
// Authors a priority.Rule via:
//   "When [p1] is requested and the holder is [p2,p3], [Preempt]."
// Plus advanced section for min_hold_seconds + per-side tool filter.
//
// Mirrors the Policy builder pattern (state object, popovers, save/
// delete/edit handlers, list-filter chips) but uses pri-* DOM IDs
// to avoid collision. Both builders share the #builder-popover host
// element so showBuilderPopover/hideBuilderPopover are imported
// from policy-builder.js (will become a separate shared util once
// both builders are out of bootstrap.js).
//
// Also owns the priority-list filter chip clicks + filter input
// + the handlePriorityReload that the Policy page's Reload button
// calls (the Priority page doesn't have its own Reload button; the
// shared one lives near the Policy header).
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 1b).
import { priorityState, priorityListFilter } from "./page-state.js";
import { apiFetch } from "./api.js";
import { escapeHTML, humanList } from "./util.js";
import { showToast, showConfirm } from "./ui.js";
import { renderPriorityPage } from "./pages/priority.js";
import { showBuilderPopover, hideBuilderPopover } from "./policy-builder.js";

const priBuilderState = {
  name: "",
  whenPriorities: [], // string[] — task_priority values for the acquirer
  heldByPriorities: [], // string[] — task_priority values for the holder
  preempt: true, // true = preempts, false = no-op (rare; whitelist)
  reason: "",
  rule: null,
  editingName: null,
};

export function openPriorityBuilder() {
  const modal = document.getElementById("priority-builder-modal");
  if (!modal) return;
  priBuilderState.name = "";
  priBuilderState.whenPriorities = [];
  priBuilderState.heldByPriorities = [];
  priBuilderState.preempt = true;
  priBuilderState.reason = "";
  priBuilderState.rule = null;
  priBuilderState.editingName = null;
  clearPriBuilderForm();
  setPriBuilderTitle("New priority rule");
  modal.style.display = "flex";
  renderPriBuilderState();
}

export function openPriorityBuilderForEdit(rule) {
  const modal = document.getElementById("priority-builder-modal");
  if (!modal) return;
  priBuilderState.editingName = rule.name;
  clearPriBuilderForm();
  seedPriorityFormFromRule(rule);
  setPriBuilderTitle("Edit priority rule");
  modal.style.display = "flex";
  renderPriBuilderState();
}

function setPriBuilderTitle(title) {
  const el = document.getElementById("priority-builder-title");
  if (el) el.textContent = title;
}

function clearPriBuilderForm() {
  document.getElementById("pri-builder-name").value = "";
  document.getElementById("pri-builder-name").readOnly = false;
  document.getElementById("pri-builder-reason").value = "";
  document.getElementById("pri-min-hold").value = "";
  document.getElementById("pri-tool-when").value = "";
  document.getElementById("pri-tool-heldby").value = "";
  document.getElementById("priority-builder-error").textContent = "";
}

function seedPriorityFormFromRule(rule) {
  priBuilderState.name = rule.name || "";
  priBuilderState.reason = rule.reason || "";
  priBuilderState.preempt = rule.preempt !== false;
  priBuilderState.whenPriorities = (rule.when?.task_priority || []).slice();
  priBuilderState.heldByPriorities = (
    rule.held_by?.task_priority || []
  ).slice();
  document.getElementById("pri-builder-name").value = rule.name || "";
  document.getElementById("pri-builder-name").readOnly = true;
  document.getElementById("pri-builder-reason").value = rule.reason || "";
  if (rule.min_hold_seconds)
    document.getElementById("pri-min-hold").value = String(
      rule.min_hold_seconds,
    );
  if (rule.when?.tool?.length)
    document.getElementById("pri-tool-when").value = rule.when.tool.join(", ");
  if (rule.held_by?.tool?.length)
    document.getElementById("pri-tool-heldby").value =
      rule.held_by.tool.join(", ");
}

function closePriorityBuilder() {
  const modal = document.getElementById("priority-builder-modal");
  if (modal) modal.style.display = "none";
  hideBuilderPopover();
}

function renderPriBuilderState() {
  const decLabel = document.getElementById("pri-decision-label");
  if (decLabel)
    decLabel.textContent = priBuilderState.preempt
      ? "Preempt"
      : "Do not preempt";
  renderPriChipsHost(
    "pri-when-priority-host",
    "pri-when-priority-empty",
    priBuilderState.whenPriorities,
    "pri-remove-when-priority",
    "any task",
  );
  renderPriChipsHost(
    "pri-heldby-priority-host",
    "pri-heldby-priority-empty",
    priBuilderState.heldByPriorities,
    "pri-remove-heldby-priority",
    "any holder",
  );
  const reasonEl = document.getElementById("pri-builder-reason");
  if (reasonEl && reasonEl.value !== priBuilderState.reason)
    reasonEl.value = priBuilderState.reason;
  const nameEl = document.getElementById("pri-builder-name");
  if (nameEl && !nameEl.readOnly && !nameEl.value.trim()) {
    nameEl.placeholder = autoNamePriorityFromState();
  }
  renderPriPreviewSentence();
}

function renderPriChipsHost(hostId, emptyId, values, removeAttr, emptyText) {
  const host = document.getElementById(hostId);
  if (!host) return;
  if (values.length === 0) {
    host.innerHTML =
      '<span id="' +
      emptyId +
      '" style="color: var(--text-tertiary); font-style: italic;">' +
      escapeHTML(emptyText) +
      "</span>";
  } else {
    host.innerHTML = values
      .map(
        (v) =>
          '<span class="builder-chip" style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 4px 2px 10px; background: var(--bg-panel-solid); border: 1px solid var(--border); border-radius: var(--r-md); font-size: 14px; font-family: monospace;">' +
          escapeHTML(v) +
          '<button type="button" data-' +
          removeAttr +
          '="' +
          escapeHTML(v) +
          '" aria-label="Remove ' +
          escapeHTML(v) +
          '" style="background: none; border: none; cursor: pointer; color: var(--text-tertiary); font-size: 16px; line-height: 1; padding: 0 4px;">×</button>' +
          "</span>",
      )
      .join(" ");
  }
}

function renderPriPreviewSentence() {
  const el = document.getElementById("pri-preview-sentence");
  if (!el) return;
  const whoWants = priBuilderState.whenPriorities.length
    ? humanList(
        priBuilderState.whenPriorities.map(
          (p) => "<code>" + escapeHTML(p) + "</code>",
        ),
      ) + " task"
    : "any task";
  const whoHolds = priBuilderState.heldByPriorities.length
    ? humanList(
        priBuilderState.heldByPriorities.map(
          (p) => "<code>" + escapeHTML(p) + "</code>",
        ),
      ) + " task"
    : "any holder";
  const verb = priBuilderState.preempt
    ? "<strong>preempt</strong> the holder"
    : "<strong>do not preempt</strong> (let the holder finish)";
  el.innerHTML =
    "When <strong>" +
    whoWants +
    "</strong> is requested and the holder is <strong>" +
    whoHolds +
    "</strong>, " +
    verb +
    ".";
}

function autoNamePriorityFromState() {
  const when = priBuilderState.whenPriorities.join("-") || "any";
  const held = priBuilderState.heldByPriorities.join("-") || "any";
  const verb = priBuilderState.preempt ? "preempts" : "allows";
  let name = (when + "-" + verb + "-" + held)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  if (!name || name === verb) name = "pri-rule-" + Date.now();
  return name.slice(0, 64);
}

function openPriPriorityPopover(triggerEl, mode) {
  // mode = 'when' | 'heldby'
  const current =
    mode === "when"
      ? priBuilderState.whenPriorities
      : priBuilderState.heldByPriorities;
  const selected = new Set(current);
  const presets = ["p0", "p1", "p2", "p3"];
  let html =
    '<div style="font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-tertiary); font-weight: 600; padding: 4px 12px;">Priorities</div>';
  for (const p of presets) {
    const on = selected.has(p);
    html +=
      '<button type="button" data-pri-toggle-priority="' +
      escapeHTML(p) +
      '" data-pri-mode="' +
      mode +
      '" style="display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 12px; background: transparent; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit; font-size: 13px;">' +
      '<span style="display: inline-block; width: 14px; height: 14px; border: 1px solid var(--border-strong); border-radius: 3px; background: ' +
      (on ? "var(--accent)" : "transparent") +
      '; color: white; font-size: 11px; line-height: 14px; text-align: center;">' +
      (on ? "✓" : "") +
      "</span>" +
      '<span style="font-family: monospace;">' +
      escapeHTML(p) +
      "</span>" +
      "</button>";
  }
  html +=
    '<div style="border-top: 1px solid var(--border); margin-top: 6px; padding: 8px 12px;">';
  html +=
    '<label style="display: block; font-size: 11px; color: var(--text-tertiary); margin-bottom: 4px;">Custom priority label</label>';
  html +=
    '<input type="text" id="pri-pop-custom-' +
    mode +
    '" placeholder="e.g. urgent, low" style="width: 100%; padding: 6px 8px; border: 1px solid var(--border); border-radius: var(--r-sm); background: var(--bg-panel-solid); color: var(--text); font-size: 12.5px; font-family: monospace; box-sizing: border-box;" />';
  html +=
    '<button type="button" data-pri-custom-add="' +
    mode +
    '" class="btn btn-sm" style="margin-top: 6px; font-size: 12px;">Add</button>';
  html += "</div>";
  showBuilderPopover(triggerEl, html);
}

function openPriDecisionPopover(triggerEl) {
  const cur = priBuilderState.preempt;
  let html = "";
  html +=
    '<button type="button" data-pri-set-decision="preempt" style="display: block; width: 100%; text-align: left; padding: 10px 12px; background: ' +
    (cur ? "var(--accent-bg)" : "transparent") +
    '; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit;">' +
    '<div style="font-weight: 600; font-size: 13px;">Preempt the holder</div>' +
    '<div style="font-size: 12px; color: var(--text-secondary);">Take the lease away from the lower-priority holder.</div>' +
    "</button>";
  html +=
    '<button type="button" data-pri-set-decision="no-op" style="display: block; width: 100%; text-align: left; padding: 10px 12px; background: ' +
    (!cur ? "var(--accent-bg)" : "transparent") +
    '; color: var(--text); border: none; border-radius: var(--r-sm); cursor: pointer; font-family: inherit;">' +
    '<div style="font-weight: 600; font-size: 13px;">Do not preempt</div>' +
    '<div style="font-size: 12px; color: var(--text-secondary);">Let the holder finish — used as a whitelist exception above a broader preempt rule.</div>' +
    "</button>";
  showBuilderPopover(triggerEl, html);
}

function buildPriorityRuleFromState() {
  const nameEl = document.getElementById("pri-builder-name");
  let name = (nameEl?.value || "").trim();
  if (!name) name = autoNamePriorityFromState();
  const reason = (
    document.getElementById("pri-builder-reason")?.value || ""
  ).trim();
  const minHold = parseInt(
    document.getElementById("pri-min-hold")?.value || "0",
    10,
  );
  const toolWhen = (
    document.getElementById("pri-tool-when")?.value || ""
  ).trim();
  const toolHeldby = (
    document.getElementById("pri-tool-heldby")?.value || ""
  ).trim();

  const rule = {
    name: name,
    preempt: !!priBuilderState.preempt,
  };
  if (reason) rule.reason = reason;
  if (minHold > 0) rule.min_hold_seconds = minHold;

  const when = {};
  if (priBuilderState.whenPriorities.length)
    when.task_priority = priBuilderState.whenPriorities.slice();
  if (toolWhen)
    when.tool = toolWhen.split(",").map((s) => s.trim()).filter(Boolean);
  if (Object.keys(when).length) rule.when = when;

  const heldBy = {};
  if (priBuilderState.heldByPriorities.length)
    heldBy.task_priority = priBuilderState.heldByPriorities.slice();
  if (toolHeldby)
    heldBy.tool = toolHeldby.split(",").map((s) => s.trim()).filter(Boolean);
  if (Object.keys(heldBy).length) rule.held_by = heldBy;

  return rule;
}

async function handlePriorityBuilderSave(btn) {
  const rule = buildPriorityRuleFromState();
  if (!rule.reason || !rule.reason.trim()) {
    document.getElementById("priority-builder-error").textContent =
      "Tell us why — the reason field is required.";
    return;
  }
  priBuilderState.rule = rule;
  btn.disabled = true;
  btn.textContent = "Saving…";
  document.getElementById("priority-builder-error").textContent = "";
  try {
    const isEdit = !!priBuilderState.editingName;
    const url = isEdit
      ? "/v1/priority/draft/" + encodeURIComponent(priBuilderState.editingName)
      : "/v1/priority/draft/append";
    const method = isEdit ? "PUT" : "POST";
    const resp = await apiFetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rule: rule }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      document.getElementById("priority-builder-error").textContent =
        "✗ Save failed: " +
        (data.message || data.error || "HTTP " + resp.status);
      btn.disabled = false;
      btn.textContent = "Save rule";
      return;
    }
    closePriorityBuilder();
    priorityState.data = null;
    await renderPriorityPage();
    showToast(
      "success",
      'Priority rule "' +
        rule.name +
        '" ' +
        (isEdit ? "updated" : "saved") +
        ".",
    );
  } catch (e) {
    document.getElementById("priority-builder-error").textContent =
      "✗ Save request failed: " + (e.message || e);
    btn.disabled = false;
    btn.textContent = "Save rule";
  }
}

async function handlePriorityRuleDelete(name, btn) {
  const ok = await showConfirm(
    'Delete priority rule "' + name + '"?',
    "This removes the rule from your priority ruleset. Future conflicts will fall through to whatever rule is next in line.",
    "Delete rule",
  );
  if (!ok) return;
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Deleting…";
  try {
    const resp = await apiFetch(
      "/v1/priority/draft/" + encodeURIComponent(name),
      { method: "DELETE" },
    );
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      btn.disabled = false;
      btn.textContent = orig;
      showToast(
        "error",
        "Delete failed: " + (data.message || data.error || "unknown error"),
      );
      return;
    }
    priorityState.data = null;
    await renderPriorityPage();
    showToast("success", 'Priority rule "' + name + '" deleted.');
  } catch (e) {
    btn.disabled = false;
    btn.textContent = orig;
    showToast("error", "Delete request failed: " + (e.message || e));
  }
}

function handlePriorityRuleEdit(name) {
  const data = priorityState.data || {};
  const rules = data.rules || [];
  const rule = rules.find((r) => r.name === name);
  if (!rule) {
    showToast("error", 'Could not find rule "' + name + '".');
    return;
  }
  openPriorityBuilderForEdit(rule);
}

// handlePriorityReload is the click handler for the Priority page's
// Reload button (which lives near the Policy page's Reload — same
// shared header). POSTs to /v1/priority/reload and refreshes the
// list with the change-count toast.
export async function handlePriorityReload(btn) {
  btn.disabled = true;
  const orig = btn.textContent;
  btn.textContent = "Reloading…";
  try {
    const resp = await apiFetch("/v1/priority/reload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text || "HTTP " + resp.status);
    }
    let summary = "Priority ruleset reloaded.";
    try {
      const data = await resp.json();
      const changes = (data && data.changes) || [];
      if (changes.length === 0) {
        summary = "Priority reloaded — no changes (file was identical).";
      } else {
        summary =
          "Priority reloaded — " +
          changes.length +
          " change" +
          (changes.length === 1 ? "" : "s") +
          ".";
      }
    } catch (_) {
      // Older daemon won't have changes.
    }
    priorityState.data = null;
    await renderPriorityPage();
    btn.disabled = false;
    btn.textContent = orig;
    showToast("success", summary);
  } catch (err) {
    btn.disabled = false;
    btn.textContent = orig;
    showToast("error", "Reload failed: " + (err.message || err));
  }
}

// ---- module init: wire click + input delegation ----

function wireListeners() {
  document.addEventListener("click", (e) => {
    // + Create rule
    if (e.target && e.target.id === "priority-create-btn") {
      openPriorityBuilder();
      return;
    }
    // Cancel / close
    if (
      e.target.id === "priority-builder-close" ||
      e.target.id === "priority-builder-cancel"
    ) {
      closePriorityBuilder();
      return;
    }
    // Save
    if (e.target.id === "priority-builder-save") {
      handlePriorityBuilderSave(e.target);
      return;
    }
    // Decision trigger
    if (e.target.closest("#pri-decision-trigger")) {
      openPriDecisionPopover(e.target.closest("#pri-decision-trigger"));
      return;
    }
    // Decision pick
    const setDec = e.target.closest("[data-pri-set-decision]");
    if (setDec) {
      priBuilderState.preempt = setDec.dataset.priSetDecision === "preempt";
      hideBuilderPopover();
      renderPriBuilderState();
      return;
    }
    // + priority (when / heldby)
    if (e.target.id === "pri-add-when-priority") {
      openPriPriorityPopover(e.target, "when");
      return;
    }
    if (e.target.id === "pri-add-heldby-priority") {
      openPriPriorityPopover(e.target, "heldby");
      return;
    }
    // Toggle priority in popover
    const togPri = e.target.closest("[data-pri-toggle-priority]");
    if (togPri) {
      const v = togPri.dataset.priTogglePriority;
      const mode = togPri.dataset.priMode;
      const arr =
        mode === "when"
          ? priBuilderState.whenPriorities
          : priBuilderState.heldByPriorities;
      const i = arr.indexOf(v);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(v);
      renderPriBuilderState();
      // Re-render popover so the checkbox state updates.
      openPriPriorityPopover(
        document.getElementById(
          mode === "when" ? "pri-add-when-priority" : "pri-add-heldby-priority",
        ),
        mode,
      );
      return;
    }
    // Custom priority add
    const custAdd = e.target.closest("[data-pri-custom-add]");
    if (custAdd) {
      const mode = custAdd.dataset.priCustomAdd;
      const inp = document.getElementById("pri-pop-custom-" + mode);
      const v = (inp?.value || "").trim();
      if (v) {
        const arr =
          mode === "when"
            ? priBuilderState.whenPriorities
            : priBuilderState.heldByPriorities;
        if (!arr.includes(v)) arr.push(v);
        renderPriBuilderState();
      }
      hideBuilderPopover();
      return;
    }
    // Chip remove (when)
    const remWhen = e.target.closest("[data-pri-remove-when-priority]");
    if (remWhen) {
      const v = remWhen.dataset.priRemoveWhenPriority;
      priBuilderState.whenPriorities = priBuilderState.whenPriorities.filter(
        (x) => x !== v,
      );
      renderPriBuilderState();
      return;
    }
    // Chip remove (heldby)
    const remHB = e.target.closest("[data-pri-remove-heldby-priority]");
    if (remHB) {
      const v = remHB.dataset.priRemoveHeldbyPriority;
      priBuilderState.heldByPriorities =
        priBuilderState.heldByPriorities.filter((x) => x !== v);
      renderPriBuilderState();
      return;
    }
    // Card buttons
    const editBtn = e.target.closest(".priority-rule-edit-btn");
    if (editBtn) {
      handlePriorityRuleEdit(editBtn.dataset.ruleName || "");
      return;
    }
    const delBtn = e.target.closest(".priority-rule-delete-btn");
    if (delBtn) {
      handlePriorityRuleDelete(delBtn.dataset.ruleName || "", delBtn);
      return;
    }
    // Filter chips + clear + row toggle (mirrors Policy list).
    const priFilterChip = e.target.closest("[data-priority-filter-decision]");
    if (priFilterChip) {
      priorityListFilter.decision = priFilterChip.dataset.priorityFilterDecision;
      renderPriorityPage();
      return;
    }
    if (e.target && e.target.id === "priority-filter-clear") {
      priorityListFilter.text = "";
      priorityListFilter.decision = "all";
      renderPriorityPage();
      return;
    }
    const priToggle = e.target.closest(".priority-rule-toggle");
    if (priToggle) {
      const name = priToggle.dataset.ruleName || "";
      if (priorityListFilter.expanded.has(name))
        priorityListFilter.expanded.delete(name);
      else priorityListFilter.expanded.add(name);
      renderPriorityPage();
      return;
    }
    const priSentence = e.target.closest(".priority-rule-sentence");
    if (priSentence) {
      const name = priSentence.dataset.ruleName || "";
      if (priorityListFilter.expanded.has(name))
        priorityListFilter.expanded.delete(name);
      else priorityListFilter.expanded.add(name);
      renderPriorityPage();
      return;
    }
  });

  document.addEventListener("input", (e) => {
    if (e.target && e.target.id === "pri-builder-reason") {
      priBuilderState.reason = e.target.value;
      renderPriPreviewSentence();
    }
    if (e.target && e.target.id === "priority-list-filter") {
      priorityListFilter.text = e.target.value || "";
      renderPriorityPage();
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
